import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator, Platform, Alert } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { observer } from '@legendapp/state/react';
import { Linking } from 'react-native';
import { supabase } from '../../services/supabase';
import { updateUserPassword } from '../../services/authService';
import { AuthServicesLogger } from '../../utils/logger';
import { useWebAlert } from '../../components/WebAlert';
import FormFieldError from '../../components/FormFieldError';
import WebLoadingOverlay from '../../components/WebLoadingOverlay';
import theme from '../../config/theme';
import { processAuthURL } from '../../services/authLinks';
import { userActions } from '../../state/globalState';
import { globalState } from '../../state/globalState';

// Logger específico para esta tela
const logger = AuthServicesLogger.PasswordReset;

const PasswordResetScreen = observer(() => {
  const router = useRouter();
  const { showAlert } = useWebAlert();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  
  // Controle de visibilidade das senhas
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Erros de validação para cada campo
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Novo: flag global para modo de recuperação
  const isPasswordRecovery = globalState.user.isPasswordRecovery.get();

  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK' }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg);
    }
  };

  // Inicialização da tela
  useEffect(() => {
    logger.info('Tela de recuperação de senha inicializada');
    
    // Processar URL inicial no mobile
    if (Platform.OS !== 'web') {
      const getUrlAndSession = async () => {
        try {
          const url = await Linking.getInitialURL();
          if (url) {
            logger.info(`URL inicial de recuperação de senha: ${url}`);
            
            // Verificar se a URL contém indicação de erro
            if (url.includes('error=') || url.includes('error_code=')) {
              const regex = /error_description=([^&]+)/;
              const match = url.match(regex);
              const errorMsg = match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : 'Link inválido ou expirado';
              
              logger.error(`Erro no link de recuperação: ${errorMsg}`);
              setLinkError(`O link de recuperação é inválido ou expirou. Por favor, solicite um novo link.`);
              return;
            }
            
            // Usar o processAuthURL existente
            const success = await processAuthURL(url);
            
            if (!success) {
              logger.error('Falha ao processar URL de recuperação de senha');
              setLinkError('Não foi possível processar o link de recuperação de senha. Por favor, solicite um novo link.');
            }
          }
        } catch (error) {
          logger.error('Erro ao processar URL de recuperação de senha:', error);
          setLinkError('Ocorreu um erro ao processar o link de recuperação. Por favor, tente novamente ou solicite um novo link.');
        }
      };
      
      getUrlAndSession();
    } else {
      // WEB: extrair parâmetros da URL e setar modo de recuperação se necessário
      try {
        const fullUrl = window.location.href;
        logger.info(`URL completa na web para reset de senha: ${fullUrl}`);
        // Extrai query e hash separadamente
        const searchParams = new URLSearchParams(window.location.search);
        const hash = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : window.location.hash;
        const hashParams = new URLSearchParams(hash);
        // Checa em ambos
        const typeRecovery = searchParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery';
        if (typeRecovery) {
          logger.info('Detectado type=recovery na URL (web), ativando modo de recuperação de senha');
          userActions.setPasswordRecoveryMode(true);
        }
      } catch (error) {
        logger.error('Erro ao processar parâmetros da URL na web:', error);
      }
    }
    
    // Registrar listener para evento de recuperação de senha
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      logger.info(`Evento de autenticação detectado: ${event}`);
      
      if (event === 'PASSWORD_RECOVERY') {
        logger.info('Evento de recuperação de senha detectado, mostrando formulário');
        setShowForm(true);
      }
    });
    
    // Limpeza ao desmontar o componente
    return () => {
      listener.subscription.unsubscribe();
      logger.info('Listener de eventos de autenticação removido');
    };
  }, []);
  
  // Validar senha
  const validatePassword = (password: string): boolean => {
    if (!password) {
      setNewPasswordError('Campo obrigatório');
      return false;
    } else if (password.length < 6) {
      setNewPasswordError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    setNewPasswordError(null);
    return true;
  };
  
  // Validar confirmação de senha
  const validateConfirmPassword = (password: string, confirmPassword: string): boolean => {
    if (!confirmPassword) {
      setConfirmPasswordError('Campo obrigatório');
      return false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError('As senhas não coincidem');
      return false;
    }
    setConfirmPasswordError(null);
    return true;
  };
  
  // Validar formulário completo
  const validateForm = (): boolean => {
    const isPasswordValid = validatePassword(newPassword);
    const isConfirmPasswordValid = validateConfirmPassword(newPassword, confirmPassword);
    
    return isPasswordValid && isConfirmPasswordValid;
  };
  
  // Manipuladores de entrada
  const handleNewPasswordChange = (text: string) => {
    setNewPassword(text);
    validatePassword(text);
    validateConfirmPassword(text, confirmPassword);
    setFormError(null);
  };
  
  const handleConfirmPasswordChange = (text: string) => {
    setConfirmPassword(text);
    validateConfirmPassword(newPassword, text);
    setFormError(null);
  };
  
  // Voltar para a tela de login
  const handleBackToLogin = async () => {
    logger.info('Voltando para a tela de login e limpando modo de recuperação de senha');
    userActions.setPasswordRecoveryMode(false);
    logger.info('Deslogando o usuário antes de redirecionar para a tela de login');
    await supabase.auth.signOut();
    router.replace('/(auth)/login');
  };
  
  // Atualizar senha do usuário
  const handleUpdatePassword = async () => {
    logger.info('Iniciando processo de atualização de senha');
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setFormError(null);
    
    try {
      const { success, error } = await updateUserPassword(newPassword);
      
      if (error || !success) {
        logger.error('Erro ao redefinir senha:', error);
        setFormError(typeof error === 'object' && error !== null ? 
          (error as any).message || 'Não foi possível redefinir a senha' : 
          'Não foi possível redefinir a senha');
        showMessage('Erro', 'Não foi possível redefinir sua senha. Verifique se o link de recuperação ainda é válido.');
      } else {
        logger.info('Senha redefinida com sucesso');
        // Limpar o modo de recuperação de senha
        userActions.setPasswordRecoveryMode(false);
        showMessage('Senha redefinida', 'Sua senha foi redefinida com sucesso. Agora você pode fazer login com a nova senha.');
        
        logger.info('Deslogando o usuário antes de redirecionar para a tela de login');
        // Deslogar o usuário antes de redirecionar
        await supabase.auth.signOut();
        // Redirecionar para a tela de login após alguns segundos
        setTimeout(() => {
          router.replace('/(auth)/login');
        }, 2000);
      }
    } catch (error) {
      logger.error('Exceção ao redefinir senha:', error);
      setFormError('Ocorreu um erro inesperado. Tente novamente mais tarde.');
      showMessage('Erro', 'Ocorreu um erro inesperado. Tente novamente mais tarde.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Renderizar conteúdo com base no estado
  const renderContent = () => {
    if (linkError) {
      return (
        <View style={styles.waitingContainer}>
          <Ionicons name="alert-circle" size={60} color={theme.colors.util.error} />
          <Text style={styles.errorText}>{linkError}</Text>
          <TouchableOpacity 
            style={[styles.button, styles.errorButton]} 
            onPress={handleBackToLogin}
          >
            <View style={styles.buttonContent}>
              <Ionicons name="arrow-back-outline" size={18} color={theme.colors.text.inverse} />
              <Text style={styles.buttonText}>Voltar para o Login</Text>
            </View>
          </TouchableOpacity>
        </View>
      );
    }
    
    if (!(showForm || isPasswordRecovery)) {
      return (
        <View style={styles.waitingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary.default} />
          <Text style={styles.waitingText}>
            Verificando seu link de recuperação...
          </Text>
        </View>
      );
    }
    
    return (
      <View style={styles.form}>
        <Text style={styles.label}>Nova Senha</Text>
        <View style={styles.inputContainer}>
          <Ionicons 
            name="lock-closed-outline" 
            size={20} 
            color={newPasswordError ? theme.colors.util.error : theme.colors.text.secondary} 
            style={styles.inputIcon}
          />
          <TextInput
            style={[
              styles.input, 
              newPasswordError ? styles.inputError : null,
              { flex: 1 }
            ]}
            value={newPassword}
            onChangeText={handleNewPasswordChange}
            placeholder="Digite sua nova senha"
            placeholderTextColor={theme.colors.text.tertiary}
            secureTextEntry={!showNewPassword}
          />
          <TouchableOpacity 
            style={styles.eyeIcon}
            onPress={() => setShowNewPassword(!showNewPassword)}
          >
            <Ionicons 
              name={showNewPassword ? "eye-off-outline" : "eye-outline"} 
              size={22} 
              color={theme.colors.text.secondary} 
            />
          </TouchableOpacity>
        </View>
        <FormFieldError message={newPasswordError} />
        
        <Text style={styles.label}>Confirmar Nova Senha</Text>
        <View style={styles.inputContainer}>
          <Ionicons 
            name="lock-closed-outline" 
            size={20} 
            color={confirmPasswordError ? theme.colors.util.error : theme.colors.text.secondary} 
            style={styles.inputIcon}
          />
          <TextInput
            style={[
              styles.input, 
              confirmPasswordError ? styles.inputError : null,
              { flex: 1 }
            ]}
            value={confirmPassword}
            onChangeText={handleConfirmPasswordChange}
            placeholder="Confirme sua nova senha"
            placeholderTextColor={theme.colors.text.tertiary}
            secureTextEntry={!showConfirmPassword}
          />
          <TouchableOpacity 
            style={styles.eyeIcon}
            onPress={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            <Ionicons 
              name={showConfirmPassword ? "eye-off-outline" : "eye-outline"} 
              size={22} 
              color={theme.colors.text.secondary} 
            />
          </TouchableOpacity>
        </View>
        <FormFieldError message={confirmPasswordError} />

        {formError && (
          <View style={styles.formErrorContainer}>
            <Ionicons name="alert-circle" size={18} color={theme.colors.util.error} />
            <Text style={styles.formErrorText}>{formError}</Text>
          </View>
        )}
        
        <TouchableOpacity 
          style={styles.button} 
          onPress={handleUpdatePassword} 
          disabled={isLoading}
        >
          {isLoading && Platform.OS !== 'web' ? (
            <ActivityIndicator color={theme.colors.text.inverse} />
          ) : (
            <View style={styles.buttonContent}>
              <Ionicons name="save-outline" size={18} color={theme.colors.text.inverse} />
              <Text style={styles.buttonText}>Redefinir Senha</Text>
            </View>
          )}
        </TouchableOpacity>
        
        <TouchableOpacity 
          onPress={handleBackToLogin} 
          style={styles.loginContainer}
        >
          <Ionicons name="log-in-outline" size={16} color={theme.colors.primary.default} />
          <Text style={styles.loginText}>
            Lembrou sua senha? <Text style={styles.loginLink}>Voltar para o login</Text>
          </Text>
        </TouchableOpacity>
      </View>
    );
  };
  
  return (
    <View style={styles.container}>
      {/* Overlay de carregamento para web */}
      <WebLoadingOverlay 
        visible={isLoading && Platform.OS === 'web'} 
        message="Redefinindo senha... Por favor, aguarde."
      />
      
      <View style={styles.headerContainer}>
        <Text style={styles.title}>Redefinir Senha</Text>
        <Text style={styles.subtitle}>
          {showForm 
            ? 'Digite sua nova senha abaixo' 
            : linkError 
              ? 'Não foi possível validar seu link'
              : 'Aguardando confirmação do link de recuperação...'}
        </Text>
      </View>
      
      {renderContent()}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
    backgroundColor: theme.colors.background.primary,
  },
  headerContainer: {
    marginBottom: 32,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 18,
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  waitingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  waitingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  errorText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.util.error,
    textAlign: 'center',
    marginBottom: 24,
  },
  errorButton: {
    backgroundColor: theme.colors.util.error,
    marginTop: 16,
  },
  form: {
    width: '100%',
  },
  label: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  inputIcon: {
    marginLeft: 12,
  },
  eyeIcon: {
    padding: 12,
  },
  input: {
    padding: 16,
    fontSize: 16,
    color: theme.colors.text.primary,
    flex: 1,
  },
  inputError: {
    borderColor: theme.colors.util.error,
  },
  formErrorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: theme.spacing.sm,
    justifyContent: 'center',
  },
  formErrorText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.md,
    marginLeft: theme.spacing.xs,
  },
  button: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    marginTop: 16,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: theme.colors.text.inverse,
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  loginContainer: {
    marginTop: 20,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  loginText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    marginLeft: 8,
  },
  loginLink: {
    color: theme.colors.primary.default,
    fontWeight: 'bold',
  },
});

export default PasswordResetScreen;
